import { Button, Divider, Form, Input, Modal, Select } from "antd";
import { Icon } from "@iconify/react";
import { Formik } from "formik";

import { SorItem } from "../../Constants";

/** One SOR item as Manage Catalog -> SOR ID configures it. */
export interface SorCatalogEntry {
  sorId: string;
  itemName: string;
  description: string;
  uom?: string;
  /** The catalog calls this `value`; a work order line calls it the Rate. */
  rate: number | string;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  onSubmit: (item: Omit<SorItem, "key">) => void;
  /** SOR catalog, fed from `/sorItem/get-sor-list` by the caller. */
  sorOptions?: SorCatalogEntry[];
  /** When set, the modal edits this line instead of adding a new one. */
  initialValues?: SorItem | null;
  /** The other lines already on the work order — the same SOR item cannot be added twice. */
  existing?: SorItem[];
}

/** Read-only fields are shaded so it is obvious they cannot be typed into. */
const readOnlyField = { background: "#f5f5f5", cursor: "not-allowed" };

interface ModalFormValues {
  sorId: string;
  itemName: string;
  quantity: string;
  rate: string;
  amount: string;
  description: string;
  uom: string;
}

const initialValues: ModalFormValues = {
  sorId: "",
  itemName: "",
  uom: "",
  quantity: "",
  rate: "",
  amount: "",
  description: "",
};

/**
 * "Procurement Approach" — adds one line to the SOR Item details table.
 *
 * A line is a QUANTITY of something the catalog defines, so only Quantity is
 * the user's to enter. Item Name, Description and Rate are read from the SOR
 * ID's entry in Manage Catalog -> SOR ID and shown read-only, and Amount is
 * Quantity x Rate.
 *
 * All four used to be free: Item Name was its own dropdown over every name in
 * the catalog, so SOR ID and Item Name could be paired at random, while
 * Description and Rate were plain text boxes. Nothing tied a line to the
 * configuration it was supposed to come from.
 */
export const ProcurementApproachModal = ({
  open,
  onCancel,
  onSubmit,
  sorOptions = [],
  initialValues: editing = null,
  existing = [],
}: Props) => {
  return (
    <Formik<ModalFormValues>
      enableReinitialize
      initialValues={
        editing
          ? (() => {
              /* Re-read the catalog: a line saved before Manage Catalog was
                 edited would otherwise keep the old name, description and
                 rate, which is exactly the drift this screen is meant to
                 prevent. Falls back to what was stored when the SOR ID is no
                 longer in the catalog — the validator reports that. */
              const configured = sorOptions.find((entry) => entry.sorId === editing.sorId);
              const rate = configured
                ? String(configured.rate ?? "")
                : String(editing.rate);
              const quantity = String(editing.quantity);
              const amount =
                quantity !== "" && rate !== "" && !isNaN(Number(quantity)) && !isNaN(Number(rate))
                  ? String(Number(quantity) * Number(rate))
                  : String(editing.amount);

              return {
                sorId: editing.sorId,
                itemName: configured?.itemName ?? editing.itemName,
                quantity,
                rate,
                amount,
                description: configured?.description ?? editing.description,
                uom: configured?.uom ?? editing.uom ?? "",
              };
            })()
          : initialValues
      }
      validate={(values) => {
        const errors: Record<string, string> = {};
        if (!values.sorId) {
          errors.sorId = "SOR ID is required";
        } else if (sorOptions.length && !sorOptions.some((entry) => entry.sorId === values.sorId)) {
          /* A line loaded from a saved work order can name a SOR ID that has
             since been removed from the catalog. Saying so beats letting it
             through as though it were still configured. */
          errors.sorId = "This SOR ID is no longer in the catalog.";
        } else if (
          /* A SOR ID can carry more than one item; the same ID and item twice is the duplicate. */
          existing.some((line) => line.sorId === values.sorId && line.itemName === values.itemName)
        ) {
          errors.itemName = "This SOR item is already added. Edit the existing line instead.";
        }
        if (!values.itemName) errors.itemName = errors.itemName ?? "SOR Item is required";
        if (values.quantity === "") {
          errors.quantity = "Quantity is required";
        } else if (Number(values.quantity) <= 0 || isNaN(Number(values.quantity))) {
          errors.quantity = "Enter a valid quantity";
        } else if (!Number.isInteger(Number(values.quantity))) {
          /* v2.1 takes quantity as an Integer; 2.5 would be refused or cut. */
          errors.quantity = "Quantity must be a whole number";
        }
        if (values.rate === "") errors.rate = "This SOR ID has no rate configured.";
        if (values.amount === "") errors.amount = "Amount is required";
        return errors;
      }}
      onSubmit={(values, { resetForm }) => {
        onSubmit({
          sorId: values.sorId,
          itemName: values.itemName,
          description: values.description,
          uom: values.uom,
          quantity: values.quantity,
          rate: values.rate,
          amount: values.amount,
        });
        resetForm();
      }}
    >
      {({ handleSubmit, handleChange, handleBlur, setFieldValue, values, touched, errors, resetForm }) => {
        const err = (field: keyof ModalFormValues) =>
          touched[field] && errors[field] ? (errors[field] as string) : undefined;

        const close = () => {
          resetForm();
          onCancel();
        };

        /* SOR Item comes first and opens straight away: every item in the
           catalog (or, once a SOR ID is chosen, the items under it). SOR ID
           then lists the ids that carry the chosen item. */
        const unique = (list: string[]) => list.filter((value, index) => value && list.indexOf(value) === index);
        const itemNames = unique(
          sorOptions.filter((entry) => !values.sorId || entry.sorId === values.sorId).map((entry) => entry.itemName)
        );
        const sorIds = unique(
          sorOptions.filter((entry) => !values.itemName || entry.itemName === values.itemName).map((entry) => entry.sorId)
        );

        /** Keep Amount in step with Quantity x Rate. */
        const recalcAmount = (quantity: string, rate: string) => {
          const q = Number(quantity);
          const r = Number(rate);
          if (quantity !== "" && rate !== "" && !isNaN(q) && !isNaN(r)) {
            setFieldValue("amount", String(q * r));
          } else {
            // Either half missing — a stale total is worse than none.
            setFieldValue("amount", "");
          }
        };

        /**
         * Take Item Name, Description and Rate from one catalog entry, and
         * recompute Amount. Called with `undefined` to clear them, so changing
         * the SOR ID never leaves another item's rate behind.
         */
        const applyEntry = (entry: SorCatalogEntry | undefined, quantity: string) => {
          if (entry) setFieldValue("sorId", entry.sorId);
          setFieldValue("itemName", entry?.itemName ?? "");
          setFieldValue("description", entry?.description ?? "");
          setFieldValue("uom", entry?.uom ?? "");
          const rate = entry?.rate === undefined || entry?.rate === null ? "" : String(entry.rate);
          setFieldValue("rate", rate);
          recalcAmount(quantity, rate);
        };

        return (
          <Modal
            title="Procurement Approach"
            open={open}
            maskClosable={false}
            centered
            width={560}
            onCancel={close}
            footer={
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button onClick={close}>Cancel</Button>
                <Button
                  type="primary"
                  onClick={() => handleSubmit()}
                >
                  Submit
                </Button>
              </div>
            }
          >
            <Divider style={{ margin: "-8px 0 20px", borderTop: "1px solid #d9d9d9" }} />
            <Form layout="vertical">
              <Form.Item required
                label="SOR Item"
                validateStatus={err("itemName") ? "error" : undefined}
                help={err("itemName")}
              >
                <Select
                  showSearch
                  placeholder="Search SOR item"
                  value={values.itemName || undefined}
                  suffixIcon={<Icon icon="ic:outline-search" />}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  filterOption={(input, option: any) =>
                    String(option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value: string) => {
                    /* An item under one SOR ID fills everything; one shared by
                       several ids waits for the SOR ID to be picked. */
                    const matches = sorOptions.filter(
                      (entry) => entry.itemName === value && (!values.sorId || entry.sorId === values.sorId)
                    );
                    if (matches.length === 1) return applyEntry(matches[0], values.quantity);
                    setFieldValue("sorId", "");
                    applyEntry(undefined, values.quantity);
                    setFieldValue("itemName", value);
                  }}
                  onBlur={handleBlur}
                >
                  {itemNames.map((name) => (
                    <Select.Option
                      value={name}
                      key={name}
                    >
                      {name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item required
                label="SOR ID"
                validateStatus={err("sorId") ? "error" : undefined}
                help={err("sorId")}
              >
                <Select
                  showSearch
                  placeholder={values.itemName ? "Choose" : "Search"}
                  value={values.sorId || undefined}
                  suffixIcon={<Icon icon="ic:outline-search" />}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                  filterOption={(input, option: any) =>
                    String(option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(value: string) => {
                    setFieldValue("sorId", value);
                    const forId = sorOptions.filter(
                      (entry) => entry.sorId === value && (!values.itemName || entry.itemName === values.itemName)
                    );
                    applyEntry(forId.length === 1 ? forId[0] : undefined, values.quantity);
                    if (forId.length !== 1) setFieldValue("sorId", value);
                  }}
                  onBlur={handleBlur}
                >
                  {sorIds.map((id) => (
                    <Select.Option
                      value={id}
                      key={id}
                    >
                      {id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="UOM">
                {/* The SOR ID's unit of measure, as Manage Catalog configures it. */}
                <Input
                  name="uom"
                  value={values.uom}
                  disabled
                  style={readOnlyField}
                  placeholder="Select a SOR item"
                />
              </Form.Item>

              <Form.Item required
                label="Quantity"
                validateStatus={err("quantity") ? "error" : undefined}
                help={err("quantity")}
              >
                <Input
                  name="quantity"
                  value={values.quantity}
                  onChange={(e) => {
                    handleChange(e);
                    recalcAmount(e.target.value, values.rate);
                  }}
                  onBlur={handleBlur}
                />
              </Form.Item>

              <Form.Item required
                label="Rate"
                validateStatus={err("rate") ? "error" : undefined}
                help={err("rate")}
              >
                {/* The catalog's configured value for this SOR ID. */}
                <Input
                  name="rate"
                  value={values.rate}
                  disabled
                  style={readOnlyField}
                  placeholder="Select a SOR item"
                />
              </Form.Item>

              <Form.Item required
                label="Amount"
                validateStatus={err("amount") ? "error" : undefined}
                help={err("amount")}
              >
                {/* Quantity x Rate. */}
                <Input
                  name="amount"
                  value={values.amount}
                  disabled
                  style={readOnlyField}
                />
              </Form.Item>

              <Form.Item
                label="Description"
                validateStatus={err("description") ? "error" : undefined}
                help={err("description")}
              >
                {/* The catalog's description for this SOR ID. */}
                <Input.TextArea
                  name="description"
                  value={values.description}
                  disabled
                  style={readOnlyField}
                  rows={4}
                  placeholder="Select a SOR item"
                />
              </Form.Item>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};
